import update from 'immutability-helper';
import { useFormContext } from 'react-hook-form';
import { usePermissions } from '@anytag/auth';
import { CustomPermissionLevel, UserRoles, useGetUserDefaultPermissionsQuery } from '@anytag/graphql';
import { Form, Radio } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';
import { SwitchToggleField } from '@anytag/shared-molecules';
import { SUPPORTED_USER_ROLES } from '@anytag/router';
import { cn } from '@anytag/shared-utils';
import { RadioGroup } from '@anyui/core';
import { UserFormValues } from './types';
import { permissionsFieldsNameMapping } from './utils';

const allPermissionTypes: Array<keyof CustomPermissionLevel> = ['noView', 'view', 'manage', 'manageAndDelete'];

interface UserPermissionsProps {
  role: UserRoles;
  isEditMode?: boolean;
  isRoleChanged?: boolean;
}
export const UserPermissions = ({ role, isEditMode, isRoleChanged }: UserPermissionsProps) => {
  const { permissions } = usePermissions();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { watch, setValue } = useFormContext<UserFormValues>();

  const permissionsValue = watch('permissions');
  const hasCustomPermissions = permissions
    ? !!permissions?.customizeUserPermissions?.manage && SUPPORTED_USER_ROLES.includes(role)
    : false;
  const isTurnedOn = !!permissionsValue?.customizeUserPermissions?.manage;

  useGetUserDefaultPermissionsQuery({
    variables: { role },
    skip: !hasCustomPermissions || (isEditMode && !isRoleChanged),
    onError: (err) => {
      enqueueSnackbar(t(err.message), { variant: 'error' });
    },
    onCompleted: ({ getUserDefaultPermissions }) => {
      if (getUserDefaultPermissions?.permissions) {
        setValue('permissions', getUserDefaultPermissions.permissions);
      }
    }
  });

  const handleClick = (field: keyof UserFormValues['permissions'], permissionToChange: keyof CustomPermissionLevel) => {
    const clickedValue = permissionsValue?.[field][permissionToChange];

    // turn on means if any other fields has value === true, we need to set it false
    if (clickedValue === false) {
      const toggleRadio = (name: keyof CustomPermissionLevel, val?: boolean | null) =>
        permissionToChange === name ? true : val !== null ? false : val;

      setValue(
        'permissions',
        update(permissionsValue, {
          [field]: {
            $apply(x: CustomPermissionLevel) {
              return {
                noView: toggleRadio('noView', x.noView),
                view: toggleRadio('view', x.view),
                manage: toggleRadio('manage', x.manage),
                manageAndDelete: toggleRadio('manageAndDelete', x.manageAndDelete)
              };
            }
          }
        })
      );
    }
  };

  const toggleCustomizePermission = (field: string, value: boolean) => {
    if (permissionsValue?.customizeUserPermissions) {
      setValue(
        'permissions',
        update(permissionsValue, {
          customizeUserPermissions: {
            $apply() {
              return {
                noView: isTurnedOn,
                view: null,
                manage: !isTurnedOn,
                manageAndDelete: null
              };
            }
          }
        })
      );
    }
  };

  if (!hasCustomPermissions || !permissionsValue) {
    return null;
  }

  return (
    <>
      <Form.SectionTitle index={3} label={t('User Permissions')} className="mt-6" />

      <SwitchToggleField
        name="permissions.customizeUserPermissions.manage"
        label={t('Customize permissions')}
        onChange={toggleCustomizePermission}
      />

      <table className="mt-3 w-full table-fixed border-collapse border-base-border bg-white text-primary">
        <thead>
          <tr
            className={cn(
              'h-10',
              '[&_th]:border-base-border [&_th]:bg-black-10 [&_th]:text-base [&_th]:font-semibold',
              '[&_th:first-of-type]:w-[30%] [&_th:first-of-type]:pl-4',
              '[&_th:not(:first-of-type)]:w-[20%] [&_th:not(:first-of-type)]:text-center'
            )}
          >
            <th>{t('Permission')}</th>
            <th>{t('No View')}</th>
            <th>{t('View')}</th>
            <th>{t('Manage')}</th>
            <th>{t('Manage and Delete')}</th>
          </tr>
        </thead>
        <tbody>
          {(Object.keys(permissionsValue || {}) as Array<keyof UserFormValues['permissions']>).map(
            (field, rowIndex) => {
              if (!permissionsValue?.[field] || field === 'customizeUserPermissions') {
                return null;
              }

              return (
                <tr key={rowIndex} className={cn('h-10 [&_td]:border-base-border')}>
                  <td className="bg-black-10 pl-4 text-base">{t(permissionsFieldsNameMapping(field))}</td>
                  {allPermissionTypes.map((permissionName, index) => {
                    const permissionValue = permissionsValue?.[field][permissionName];
                    const disabled = permissionValue === null;

                    return (
                      <td key={`${rowIndex}-${index}`} className="h-[inherit]">
                        <RadioGroup
                          className={cn(
                            'size-full',
                            'has-aria-checked:bg-blue-70/10',
                            "[&_[role='radiogroup']]:size-full [&_[role='radiogroup']]:items-center [&_[role='radiogroup']]:justify-center",
                            "[&_[role='radio']]:size-full [&_[role='radio']]:items-center [&_[role='radio']]:justify-center",
                            { 'bg-black-10': disabled }
                          )}
                          value={Object.keys(permissionsValue?.[field] || {}).find(
                            (key) => permissionsValue?.[field][key]
                          )}
                          onChange={(value) => handleClick(field, value as keyof CustomPermissionLevel)}
                        >
                          <Radio value={permissionName} disabled={disabled} className="flex! size-full" />
                        </RadioGroup>
                      </td>
                    );
                  })}
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    </>
  );
};
